import React from 'react'
import cx from 'classnames'
import flow from 'lodash/flow'
import PropTypes, { func, string, objectOf, node, number, bool } from 'prop-types'
import { DragSource, DropTarget } from 'react-dnd'
import { findDOMNode } from 'react-dom'

import Tooltip from '../tooltip/tooltip'

import styles from './assignment-editor-list-row.module.css'

const propTypes = {
    actionIcon: node,
    appendix: string,
    className: string,
    connectDragSource: func,
    connectDropTarget: func,
    contentClassName: string,
    icon: node,
    iconWrapperClassName: string,
    index: number,
    isDragging: bool,
    // eslint-disable-next-line react/forbid-prop-types
    item: objectOf(PropTypes.any).isRequired,
    listId: number,
    name: string,
    onClick: func
}

/**
 * Implements the drag source contract.
 */
const cardSource = {
    /**
     * Implements dragging handler
     * @param {object} props
     * @return {object}
     */
    beginDrag(props) {
        return {
            action: props.onClick,
            index: props.index,
            item: props.item,
            listId: props.listId
        }
    }
}

/**
 * Implements the drag target contract.
 */
const cardTarget = {
    /**
     * Implements hover handler
     * @param {object} props
     * @param {object} monitor
     * @param {React.Element} component
     */
    hover(props, monitor, component) {
        const dragIndex = monitor.getItem().index
        const hoverIndex = props.index
        const sourceListId = monitor.getItem().listId

        // Don't replace items with themselves
        if (!component || dragIndex === hoverIndex) {
            return
        }

        // Determine rectangle on screen
        const hoverBoundingRect = findDOMNode(component).getBoundingClientRect()
        const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
        const clientOffset = monitor.getClientOffset()
        const hoverClientY = clientOffset.y - hoverBoundingRect.top

        // Dragging downwards
        if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
            return
        }

        // Dragging upwards
        if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
            return
        }

        if (props.listId === sourceListId) {
            props.moveCard(dragIndex, hoverIndex)
            monitor.getItem().index = hoverIndex
        }
    }
}

/**
 * Class representing a assignment editor list row.
 * (This must be a component because of React DnD features which are injected into it.)
 * @class
 * @extends Component
 */
const AssignmentEditorListRow = ({
    actionIcon,
    appendix,
    className,
    connectDragSource,
    connectDropTarget,
    contentClassName,
    icon,
    iconWrapperClassName,
    isDragging,
    item,
    name,
    onClick
}) => {
    const combinedRowClassName = cx(styles['assignment-editor-list-row'], 't3sel-assignment-editor-list-row', {
        className,
        [styles['invisible']]: isDragging
    })
    const combinedIconWrapperClassName = cx(styles['assignment-editor-list-row-icon-wrapper'], iconWrapperClassName)

    const renderContent = (insideTooltip = false) => (
        <div className={!insideTooltip && styles['item-container']}>
            <div className={styles[insideTooltip ? 'item-name-in-tooltip' : 'item-name']}>{name}</div>
            {appendix && (
                <div className={styles[insideTooltip ? 'item-appendix-in-tooltip' : 'item-appendix']}>{appendix}</div>
            )}
        </div>
    )

    return connectDragSource(
        connectDropTarget(
            <div className={combinedRowClassName} onClick={() => onClick(item)}>
                <div className={combinedIconWrapperClassName}>
                    {icon || <div className={styles['assignment-editor-list-row-icon']} />}
                </div>
                <div className={cx(styles['assignment-editor-list-row-content'], contentClassName)}>
                    <Tooltip content={renderContent(true)} placement="top">
                        {renderContent()}
                    </Tooltip>
                    <span className={styles['item-action-icon']}>{actionIcon}</span>
                </div>
            </div>
        )
    )
}

AssignmentEditorListRow.propTypes = propTypes

// Export the wrapped component
const AssignmentEditorListRowConnected = flow(
    DropTarget('CARD', cardTarget, connect => ({
        // Injected by React DnD
        connectDropTarget: connect.dropTarget()
    })),
    DragSource('CARD', cardSource, (connect, monitor) => ({
        connectDragPreview: connect.dragPreview(),
        // Injected by React DnD
        connectDragSource: connect.dragSource(),
        isDragging: monitor.isDragging()
    }))
)(AssignmentEditorListRow)

export { AssignmentEditorListRowConnected }
